import type { Product } from '@clockwise/clients/products';
import { LifecycleStatus } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { CheckSlot, Combobox } from '@/components/custom/combobox';
import { OutOfScopeBadge } from '@/components/custom/out-of-scope-badge';
import { PickerTruncationNotice } from '@/components/custom/picker-truncation-notice';
import { ServiceRoleChip } from '@/components/custom/product-label';
import {
  type LoadStrategy,
  useEntityAutocomplete,
} from '@/components/custom/use-entity-autocomplete';
import { productsApi, serviceRolesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { warnIfTruncated } from '@/lib/warn-if-truncated';

// NB: no `active` filter. Some products legitimately reference a role that
// was later deactivated — we still want to show its chip so the picker
// remains consistent with the SOW detail page's role lookup (which uses
// the same shape and shares this query's cache). `limit` must match that
// consumer and stay at or below the server cap (`findSchema` caps `limit`
// at 500) — service-role catalogs are a small bounded set, so one page
// covers every chip. Going above the cap 400s the whole query, which
// silently drops *all* chips.
const SERVICE_ROLES_LIST_QUERY = {
  sortField: 'name',
  sortDirection: 'asc',
  limit: 500,
} as const;

// Picker for an active product. The list query filters to `status = Active`,
// but the detail fetch resolves any status so existing SOW lines can still
// render a product that's since been deactivated (with a badge).
//
// `loadStrategy` defaults to 'preload' — the full role-scoped catalog (up to
// PRODUCT_PRELOAD_LIMIT, which matches the server's 500-row cap) loads in
// one page on first open and filters in memory. A 'lazy' default showed only
// the first 20 products unless the user typed, which read as "products are
// missing" on the timesheet. If a tenant ever outgrows the cap, the dropdown
// renders a "first N of M" footer (via `auto.truncated`) so the truncation
// is visible instead of silent.
interface Props {
  value: string | null;
  onChange: (product: Product | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  loadStrategy?: LoadStrategy;
  /** Open the popover on mount. Used by inline-edit cells. */
  defaultOpen?: boolean;
  /** Forward popover open/close so a singleton-mount caller can react to
   *  click-away (no selection) and reset its editing state. */
  onOpenChange?: (open: boolean) => void;
  /** When provided as a Set, the dropdown sections by membership: items
   *  whose id is in the set render under an "In scope" header and sort to
   *  the top; everything else renders below under "Out of scope". The
   *  selected product also gets an "Out of scope" badge in the trigger
   *  when it isn't in the set.
   *
   *  Pass `undefined` (the default) for a flat list. Use `undefined` —
   *  NOT an empty `Set` — while in-scope data is still loading; an empty
   *  Set is interpreted as "fetched, nothing covers this customer" and
   *  would push every product under "Out of scope". */
  inScopeProductIds?: ReadonlySet<string>;
  /** Service-role id used to scope the catalog at fetch time
   *  (`?accessibleByRoleId=`): only products where the role is the
   *  primary OR in visibleTo are returned. (Dropdown order itself is
   *  role-code → name; see the `items` sort below.)
   *  Pass the assignee's *per-customer* role (from
   *  `customer_team_members.serviceRoleId`), NOT their global
   *  `user.serviceRoleId` — a user can serve different roles on
   *  different customers and the picker must reflect what they can
   *  actually log against on THIS customer. Server gates the same rule
   *  on write. Undefined = no filter, no sort. */
  filterServiceRoleId?: string;
  /** Multi-role variant of `filterServiceRoleId`. When set with > 1
   *  ids the picker sends `?accessibleByRoleIds=a,b,c` (CSV) to scope
   *  the catalog. Wins over `filterServiceRoleId` when both are provided.
   *
   *  Used by the timesheet week grid: a single (customer, product)
   *  row spans seven days, and the assignee may hold different
   *  per-customer roles across that span (e.g. role rotation
   *  mid-week). The grid unions the per-day memberships for the
   *  row's customer and passes the resulting set so the picker
   *  surfaces every product the user could legally log against any
   *  day in the week. The server still enforces per-day on write —
   *  picking a product allowed for only some days will 422 the
   *  off-role days the same way an off-team day already does. */
  filterServiceRoleIds?: readonly string[];
  /** Scope the catalog by the product `internal` flag (server-side filter).
   *  Pass the selected customer's `isInternal`: an internal customer offers
   *  ONLY internal products, every other customer EXCLUDES them. Leave
   *  undefined for no internal/non-internal restriction.
   *
   *  The `internal` filter composes with `filterServiceRoleId(s)`: the catalog
   *  is scoped first by `internal` and then by the assignee's service role, so
   *  an internal customer surfaces only the internal activities that role can
   *  access — the same role-availability rule applied to external customers.
   *  `inScopeProductIds` (SOW sectioning) is still ignored when `true`, since
   *  internal activities aren't carried on a SOW. The server applies the
   *  matching rule on write. */
  filterByInternal?: boolean;
  /** Scope the catalog to the cell's customer (server-side
   *  `?customerId=`): the list returns global activities PLUS this customer's
   *  custom (customer-specific) activities, and excludes every other
   *  customer's. Pass the row/cell's selected customer id; leave undefined
   *  (e.g. before a customer is chosen) to surface global activities only.
   *  The server applies the matching ownership rule on write. */
  customerId?: string | null;
  /** Activity ids the assignee has recently logged for THIS customer, ordered
   *  most-recent-first (from `useRecentProductsByCustomer`). When non-empty the
   *  dropdown grows a "Recent" band at the very top — these items in this order
   *  — above the "Assigned to you" / "Other activities" bands. Drives fast data
   *  entry: the activities you reach for most are one glance away.
   *  Undefined/empty = no Recent band. Ids not present in the loaded catalog
   *  page are ignored. */
  recentProductIds?: readonly string[];
}

// One full page of the product catalog. Must stay at or below the server cap
// (products `findSchema` caps `limit` at 500) — going above 400s the query.
const PRODUCT_PRELOAD_LIMIT = 500;

// Dropdown band headers, in render order. "Recent" = used before by you (for
// this customer); "Assigned to you" = an activity whose primary role you hold
// on this customer's team; "Other activities" = everything else the catalog
// surfaced.
const GROUP_LABELS = {
  recent: 'Recent',
  assigned: 'Assigned to you',
  other: 'Other activities',
} as const;

// Service-role seniority, junior → senior. Within a band, activities cluster
// by role in THIS order (Bookkeeper → Accountant → Controller → CFO) rather
// than alphabetically, matching how the business ranks the roles.
//
// NOTE: this is a hard-coded map on the tenant's short codes, pending a proper
// `seniority_rank` column on service_roles. Codes not listed here (a custom or
// renamed role on another tenant) fall back to AFTER the ranked ones, ordered
// alphabetically — so the ordering degrades gracefully instead of breaking.
const SERVICE_ROLE_SENIORITY = ['BK', 'AC', 'CTR', 'CFO'] as const;
const seniorityRank = (code: string): number => {
  const i = (SERVICE_ROLE_SENIORITY as readonly string[]).indexOf(code);
  return i === -1 ? SERVICE_ROLE_SENIORITY.length : i;
};

const getProductId = (p: Product) => p.id;
// Display the human name only. The billing codes are still searchable below
// so a power user can type "ACCT" and find "Accounting System Setup" — the
// SKUs just don't clutter the rendered row.
const productLabel = (p: Product) => p.name;
const productFilter = (p: Product, q: string) => {
  const hay =
    `${p.retainerCode ?? ''} ${p.tmCode ?? ''} ${p.name} ${p.description ?? ''}`.toLowerCase();
  return hay.includes(q.toLowerCase());
};
const buildListQuery = (
  q: string | undefined,
  limit: number,
  accessibleByRoleIds: readonly string[] | undefined,
  internal: boolean | undefined,
  customerId: string | null | undefined,
) => {
  // Avoid spreading `undefined` into the query record — the API client's
  // `normalize` skips them, but the React Query key includes them as-is
  // and we want stable, role-keyed cache buckets. One id collapses to the
  // singular shorthand so the cache bucket matches existing single-role
  // callers (sow lines, etc.) — same wire request, same key.
  // `internal` is included only when set so non-internal-aware callers keep
  // their existing cache bucket; both `true` and `false` are meaningful.
  // `customerId` is included only when set: present → the list returns global
  // activities PLUS that customer's custom activities (and no other
  // customer's); absent → global activities only. Non-customer callers keep
  // their existing cache bucket.
  const base = {
    q,
    status: LifecycleStatus.Active,
    sortField: 'name' as const,
    sortDirection: 'asc' as const,
    limit,
    offset: 0,
    ...(internal === undefined ? {} : { internal }),
    ...(customerId ? { customerId } : {}),
  };
  if (!accessibleByRoleIds || accessibleByRoleIds.length === 0) return base;
  if (accessibleByRoleIds.length === 1) {
    return { ...base, accessibleByRoleId: accessibleByRoleIds[0] };
  }
  // `string[]` (not readonly) to match ListProductsQuery; the client's
  // `normalize` CSV-joins arrays.
  return { ...base, accessibleByRoleIds: [...accessibleByRoleIds] };
};
const fetchProductOne = (id: string) => productsApi.get(id);
const productDetailKey = (id: string) => queryKeys.products.detail(id);

export function ProductAutocomplete({
  value,
  onChange,
  disabled,
  placeholder = 'Select activity…',
  className,
  loadStrategy = 'preload',
  defaultOpen = false,
  onOpenChange,
  inScopeProductIds,
  filterServiceRoleId,
  filterServiceRoleIds,
  filterByInternal,
  customerId,
  recentProductIds,
}: Props) {
  // Unify the singular + plural props into one canonical role list. The
  // plural form WINS when both are present — callers migrating to the
  // multi-role path shouldn't have to drop the singular prop in lockstep.
  // Dedupe + sort so the query key is stable regardless of input order
  // (the grid builds the list by walking days; a Mon-vs-Tue ordering
  // shouldn't churn the cache).
  //
  // The service-role filter applies to internal customers too: the catalog is
  // scoped first by the `internal` flag and then by the assignee's role, so an
  // internal customer offers only the internal activities that role can access
  // (mirrors the external-customer rule; server gates the same on write).
  const internal = filterByInternal === true;
  const effectiveRoleIds = useMemo<readonly string[] | undefined>(() => {
    if (filterServiceRoleIds && filterServiceRoleIds.length > 0) {
      return Array.from(new Set(filterServiceRoleIds)).sort();
    }
    if (filterServiceRoleId) return [filterServiceRoleId];
    return undefined;
  }, [filterServiceRoleId, filterServiceRoleIds]);

  // In-scope sectioning is still dropped for the internal customer: internal
  // activities aren't carried on a SOW, so every one would land "Out of scope".
  const sectionScope = internal ? undefined : inScopeProductIds;

  // Memoize closures so query keys stay referentially stable across
  // renders. React Query keys are deep-compared so this is a perf
  // micro-optimization, but it also makes the call site easier to reason
  // about when grepping for re-fetches.
  const fetchProductList = useCallback(
    ({ q, limit }: { q?: string; limit: number }) =>
      productsApi.list(buildListQuery(q, limit, effectiveRoleIds, filterByInternal, customerId)),
    [effectiveRoleIds, filterByInternal, customerId],
  );
  const productListKey = useCallback(
    ({ q, limit }: { q?: string; limit: number }) =>
      queryKeys.products.list(
        buildListQuery(q, limit, effectiveRoleIds, filterByInternal, customerId),
      ),
    [effectiveRoleIds, filterByInternal, customerId],
  );
  const auto = useEntityAutocomplete<Product>({
    value,
    strategy: loadStrategy,
    fetchList: fetchProductList,
    fetchOne: fetchProductOne,
    listKey: productListKey,
    detailKey: productDetailKey,
    preloadLimit: PRODUCT_PRELOAD_LIMIT,
  });

  // Service-role catalog is small and bounded per tenant; cache-shared with
  // other consumers (product form, user form, etc.) via the same query key.
  const serviceRoles = useQuery({
    queryKey: queryKeys.serviceRoles.list(SERVICE_ROLES_LIST_QUERY),
    queryFn: () => serviceRolesApi.list(SERVICE_ROLES_LIST_QUERY),
  });
  warnIfTruncated(serviceRoles.data, 'product-autocomplete role badges');
  const serviceRoleCodeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of serviceRoles.data?.data ?? []) map.set(r.id, r.shortCode);
    return map;
  }, [serviceRoles.data]);

  const handleOpenChange = (open: boolean) => {
    auto.setOpen(open);
    onOpenChange?.(open);
  };

  // Recent-activity ids (assignee's "used before by you" for this customer),
  // as a Set for membership tests. Undefined when the caller passes no recents
  // — the Recent band then collapses and the list falls back to the
  // Assigned/Other bands (or, if scope data is supplied, the legacy In/Out-of-
  // scope sectioning). Empty array is treated the same as undefined.
  const recentIdSet = useMemo(
    () => (recentProductIds && recentProductIds.length > 0 ? new Set(recentProductIds) : undefined),
    [recentProductIds],
  );

  // The dropdown is built as up to three CONTIGUOUS bands (Combobox emits a
  // header only when the group key changes, so band order == header order):
  //   1. Recent — the assignee's recently-used activities, kept in the
  //      server's most-recent-first order. Only ids present in the loaded
  //      catalog page appear.
  //   2. Assigned to you — activities whose PRIMARY service role is one the
  //      assignee holds on this customer (effectiveRoleIds). Everything the
  //      server returned via visibleToServiceRoleIds only (accessible, but not
  //      the user's own role) sorts below. Inert when no role filter is set.
  //   3. Other — everything else, clustered by role in seniority order
  //      (BK → AC → CTR → CFO) then name, so products sharing a role group
  //      together instead of being interspersed by SKU. Unresolved role codes
  //      (catalog still loading / deactivated role) sort last so rows don't
  //      jump as codes stream in.
  // Each band renders under its own header ("Recent" / "Assigned to you" /
  // "Other activities"); every activity appears in the HIGHEST band it
  // qualifies for (a recent own-role activity shows only under Recent). When
  // there are no recents, the legacy in-scope "In scope"/"Out of scope"
  // sectioning takes over instead.

  // Roles the assignee holds on this customer (the "assigned to you" set), as
  // a Set for the sort + the group classifier. Undefined when no role filter
  // is in play — then there's no Assigned band and the sort key is inert.
  const ownRoleIdSet = useMemo(
    () => (effectiveRoleIds && effectiveRoleIds.length > 0 ? new Set(effectiveRoleIds) : undefined),
    [effectiveRoleIds],
  );

  // Build the ordered list AND declare which grouping owns it, together, so
  // the header grouping can never disagree with the item order (a mismatch
  // would make Combobox emit repeated headers). `layout: 'bands'` = the
  // Recent/Assigned/Other grouping; `layout: 'scope'` = the legacy
  // In/Out-of-scope sectioning. The two are mutually exclusive by
  // construction: scope only wins when there are no recents to show.
  const { items, layout } = useMemo<{ items: Product[]; layout: 'bands' | 'scope' }>(() => {
    const isOwnRole = (p: Product) => ownRoleIdSet?.has(p.serviceRoleId) ?? false;
    const byOwnRoleThenRoleThenName = (a: Product, b: Product) => {
      const oa = isOwnRole(a);
      const ob = isOwnRole(b);
      if (oa !== ob) return oa ? -1 : 1;
      const ca = serviceRoleCodeById.get(a.serviceRoleId) ?? '';
      const cb = serviceRoleCodeById.get(b.serviceRoleId) ?? '';
      if (ca !== cb) {
        // Unresolved codes (catalog still loading / deactivated role) sort
        // last so rows don't jump as codes stream in.
        if (ca === '') return 1;
        if (cb === '') return -1;
        // Cluster by role in seniority order (BK → AC → CTR → CFO), not
        // alphabetically; unknown codes share the trailing bucket and break
        // ties alphabetically.
        const ra = seniorityRank(ca);
        const rb = seniorityRank(cb);
        if (ra !== rb) return ra - rb;
        return ca.localeCompare(cb);
      }
      return a.name.localeCompare(b.name);
    };

    // Band 1: recents, in the given (most-recent-first) order, deduped, and
    // restricted to what's actually loaded.
    const recent: Product[] = [];
    if (recentIdSet) {
      const byId = new Map(auto.items.map((p) => [p.id, p] as const));
      const seen = new Set<string>();
      for (const id of recentProductIds ?? []) {
        if (seen.has(id)) continue;
        const p = byId.get(id);
        if (p) {
          recent.push(p);
          seen.add(id);
        }
      }
    }

    // Bands 2+3: everything not already in the Recent band, own-role → code →
    // name.
    const rest = (
      recentIdSet ? auto.items.filter((p) => !recentIdSet.has(p.id)) : [...auto.items]
    ).sort(byOwnRoleThenRoleThenName);

    // In-scope sectioning is mutually exclusive with the fast-entry bands (two
    // different top-level groupings can't both head the list). It owns the
    // layout ONLY when there's no Recent band; partition is stable so the
    // own-role→code→name order holds within each "In scope"/"Out of scope"
    // band. Returning layout: 'scope' here forces the grouping to match this
    // ordering rather than the Recent/Assigned/Other keys.
    if (recent.length === 0 && sectionScope !== undefined) {
      const inScope: Product[] = [];
      const outScope: Product[] = [];
      for (const p of rest) {
        (sectionScope.has(p.id) ? inScope : outScope).push(p);
      }
      return { items: [...inScope, ...outScope], layout: 'scope' };
    }
    return { items: [...recent, ...rest], layout: 'bands' };
  }, [auto.items, sectionScope, serviceRoleCodeById, ownRoleIdSet, recentIdSet, recentProductIds]);

  // Classify each item into its band. Recent wins over Assigned (an activity
  // that's both shows only under Recent); Assigned requires the item's PRIMARY
  // role to be one the assignee holds. In the 'bands' layout `items` is
  // pre-sorted recent → assigned → other, so these keys come out contiguous
  // and Combobox emits exactly one header per band.
  const groupKeyOf = useCallback(
    (p: Product): 'recent' | 'assigned' | 'other' => {
      if (recentIdSet?.has(p.id)) return 'recent';
      if (ownRoleIdSet?.has(p.serviceRoleId)) return 'assigned';
      return 'other';
    },
    [recentIdSet, ownRoleIdSet],
  );

  // Group only in the 'bands' layout, and only when at least two bands are
  // actually present — a single band (e.g. every activity is "assigned") gets
  // no lonely header and reads as a flat list. Gating on `layout` keeps the
  // group keys aligned with the item order (the 'scope' layout is grouped by
  // `sectioned` below instead).
  const groupsPresent = useMemo(() => {
    const s = new Set<string>();
    for (const p of items) s.add(groupKeyOf(p));
    return s;
  }, [items, groupKeyOf]);
  const grouped = layout === 'bands' && groupsPresent.size >= 2;
  // The band the first row belongs to — its header skips the top border so the
  // divider only ever sits BETWEEN bands.
  const firstGroupKey = items[0] ? groupKeyOf(items[0]) : undefined;

  // Scope sectioning owns the display exactly when the memo laid the list out
  // for it — i.e. no Recent band. This mirrors `grouped`'s gate on `layout`,
  // so the two groupings are never both active and the scope dots/badge only
  // show when the items are actually scope-ordered. The `sectionScope`
  // conjunct is redundant at runtime (layout 'scope' implies it) but lets TS
  // narrow `sectionScope` to non-undefined wherever `sectioned` is truthy.
  const sectioned = layout === 'scope' && sectionScope !== undefined;

  return (
    <Combobox<Product>
      items={items}
      selectedItem={auto.selectedItem}
      value={value}
      onChange={onChange}
      getId={getProductId}
      getLabel={productLabel}
      filterFn={auto.isClient ? productFilter : undefined}
      onQueryChange={auto.isClient ? undefined : auto.setQuery}
      onOpenChange={handleOpenChange}
      loading={auto.loading}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      defaultOpen={defaultOpen}
      // Product names + the SKU chip are routinely longer than the picker's
      // trigger (e.g. a narrow timesheet cell). Let the popover grow past
      // the anchor width up to the Combobox's auto-cap so rows aren't
      // truncated to "Cap Ta…", "Bookke…", etc.
      popoverWidth="auto"
      // Catalog outgrew the preload page: say so instead of silently
      // presenting a partial list as complete.
      renderFooter={
        auto.truncated
          ? () => (
              <PickerTruncationNotice
                truncated={auto.truncated}
                noun="products"
                clientFiltered={auto.isClient}
              />
            )
          : undefined
      }
      // Visual role chip pinned to the left edge of the closed trigger.
      // Without this the trigger shows just the product name, so two
      // duplicate-named products would render identically after selection
      // — defeating the disambiguation the dropdown rows provide.
      renderLeading={(p) => {
        const code = serviceRoleCodeById.get(p.serviceRoleId);
        return code ? <ServiceRoleChip code={code} /> : null;
      }}
      renderTrailing={(p) => {
        if (p.status !== LifecycleStatus.Active) {
          return (
            <span className="rounded bg-muted px-1 text-[10px] text-muted-foreground">
              inactive
            </span>
          );
        }
        if (sectioned && !sectionScope.has(p.id)) {
          return <OutOfScopeBadge />;
        }
        return null;
      }}
      grouping={
        grouped
          ? {
              // `items` is pre-sorted recent → assigned → other, so these keys
              // come out contiguous and yield one header per present band.
              getKey: groupKeyOf,
              renderHeader: (groupKey) => (
                <div
                  role="presentation"
                  className={`px-3 ${groupKey === firstGroupKey ? 'pt-2' : 'border-t pt-2'} pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground`}
                >
                  {GROUP_LABELS[groupKey as keyof typeof GROUP_LABELS]}
                </div>
              ),
            }
          : sectioned
            ? {
                getKey: (p) => (sectionScope.has(p.id) ? 'in' : 'out'),
                renderHeader: (groupKey) => (
                  <div
                    role="presentation"
                    className={`px-3 ${groupKey === 'in' ? 'pt-2' : 'border-t pt-2'} pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground`}
                  >
                    {groupKey === 'in' ? 'In scope' : 'Out of scope'}
                  </div>
                ),
              }
            : undefined
      }
      renderItem={(p, { isSelected }) => {
        // Scope dot — emerald = in scope, amber = out of scope. Only shown
        // when sectioning is active; without scope data, no dot to avoid
        // a meaningless decoration.
        const dot = sectioned ? (
          <span
            aria-hidden
            data-testid={sectionScope.has(p.id) ? 'scope-dot-in' : 'scope-dot-out'}
            className={cn(
              'size-1.5 rounded-full',
              sectionScope.has(p.id) ? 'bg-emerald-500' : 'bg-amber-500',
            )}
          />
        ) : null;
        // Flex row: a fixed checkmark gutter (CheckSlot, shared across all
        // pickers) so the check never lands on top of the scope dot, then
        // the scope dot, then the role chip sitting right next to the name.
        // ServiceRoleChip caps itself at 5rem with truncation, so it never
        // shoves the name off-screen. The outer `title` puts the role on
        // hover for the whole row (chip, dot, and name) — without it, only
        // the chip would carry the tooltip, and hovering the name (the
        // natural target) would be silent.
        const primaryCode = serviceRoleCodeById.get(p.serviceRoleId);
        const primaryTitle = primaryCode ? `Primary role: ${primaryCode}` : undefined;
        return (
          <div className="flex min-w-0 items-center gap-2 pl-1" title={primaryTitle}>
            <CheckSlot isSelected={isSelected} />
            {dot}
            {primaryCode ? <ServiceRoleChip code={primaryCode} /> : null}
            <span className="truncate">{p.name}</span>
          </div>
        );
      }}
    />
  );
}
